import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import 'react-easy-crop/react-easy-crop.css';
import './BannerCropper.css';

/**
 * Utility function to create a cropped image
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

/**
 * Generates a cropped image from the cropper area
 * pixelCrop from react-easy-crop contains x, y, width, height in pixels of the original image
 */
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // react-easy-crop already maintains 16:9 aspect ratio
  // Use the crop dimensions directly
  const outputWidth = pixelCrop.width;
  const outputHeight = pixelCrop.height;
  
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  // Draw the cropped portion
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
}

function BannerCropper({ imageSrc, onCropComplete, onCancel }) {
  const { t } = useTranslation(['events', 'shared']);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    try {
      setProcessing(true);
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Convert blob to File
      const file = new File([croppedImage], 'banner.jpg', { type: 'image/jpeg' });
      
      onCropComplete(file);
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="banner-cropper-modal">
      <div className="banner-cropper-content">
        <div className="banner-cropper-header">
          <h3>{t('events.createEvent.cropBanner', 'Przycinij baner')}</h3>
          <button 
            type="button"
            className="banner-cropper-close"
            onClick={onCancel}
            disabled={processing}
          >
            ×
          </button>
        </div>
        
        <div className="banner-cropper-container">
          <div className="banner-cropper-wrapper">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9} // Proporcje 16:9
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              cropShape="rect" // Prostokątny crop area
              showGrid={false}
              minZoom={0.5}
              maxZoom={3}
            />
          </div>
        </div>

        <div className="banner-cropper-controls">
          <div className="banner-cropper-zoom-control">
            <label htmlFor="zoom-slider">
              {t('events.createEvent.zoom', 'Przybliżenie')}
            </label>
            <input
              id="zoom-slider"
              type="range"
              min={0.5}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="banner-cropper-slider"
              disabled={processing}
            />
          </div>

          <div className="banner-cropper-actions">
            <button
              type="button"
              className="banner-cropper-button banner-cropper-button-cancel"
              onClick={onCancel}
              disabled={processing}
            >
              {t('shared.common.cancel', 'Anuluj')}
            </button>
            <button
              type="button"
              className="banner-cropper-button banner-cropper-button-confirm"
              onClick={handleConfirm}
              disabled={processing || !croppedAreaPixels}
            >
              {processing ? t('shared.common.loading', 'Przetwarzanie...') : t('shared.common.confirm', 'Potwierdź')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BannerCropper;
